import type { AudioAnnotationMultiSelectField } from '@/features/nectar/api/models'
import { type MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { clsx } from 'clsx'
import { Check } from 'lucide-react'
import { FieldLabel } from '../FieldLabel'

type MultiSelectFieldProps = {
  field: AudioAnnotationMultiSelectField
  value: string[]
  onChange: (value: string[]) => void
}

const buildHint = (
  field: AudioAnnotationMultiSelectField,
  t: (descriptor: MessageDescriptor) => string
): string | undefined => {
  const min = field.minSelected
  const max = field.maxSelected
  if (min !== undefined && max !== undefined) {
    if (min === max) return t(msg`Select ${min}`)
    return t(msg`Select ${min}–${max}`)
  }
  if (min !== undefined) return t(msg`Select at least ${min}`)
  if (max !== undefined) return t(msg`Select up to ${max}`)
  return undefined
}

export const MultiSelectField = ({ field, value, onChange }: MultiSelectFieldProps) => {
  const { t } = useLingui()
  const hint = buildHint(field, t)

  const toggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue))
    } else {
      onChange([...value, optionValue])
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <FieldLabel label={field.label} description={field.description} required={field.required} hint={hint} />
      <div className="flex flex-wrap gap-2">
        {field.options.map((option) => {
          const selected = value.includes(option.value)
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => toggle(option.value)}
              className={clsx(
                'flex h-10 min-w-[88px] flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-solid px-3 text-center text-sm font-semibold transition-colors',
                selected ? 'border-green bg-green/5 text-green' : 'text-gray-70 hover:border-gray-90 border-gray-100'
              )}
            >
              {selected && <Check width="14" height="14" className="shrink-0" />}
              <span>{option.label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
